import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import denormalize from "json-api-denormalizer"
import { readEndpoint } from "redux-json-api"
import { connect } from "react-redux"
import { executeIfNotEqual } from "../../util"
import TrackProgressPage from "../../pages/track_progress/TrackProgressPage"
import TrackProgressQuestPage from "../../pages/track_progress/TrackProgressQuestPage"
import { toggleValueInList } from "../../util"
import { gradeSummary, gradeSummariesPerStudent, getPopularIncorrect } from "./grading"
import {
  setQuestId,
  setRoutingStatus,
  selectProgressOverviewShowAllContent,
  selectProgressOverviewModuleIds,
  selectProgressOverviewContentTypes,
  commitSelectedProgressOverviewFilters,
  revertSelectedProgressOverviewFilters,
  selectNgssDimensionsShowAllContent,
  selectNgssDimensionsContentType,
  selectPerformanceExpectationsShowAllContent,
  selectPerformanceExpectationsModuleIds,
  setExpeditionAssignments
} from "./actions"
import { runQuery } from "../../fetchUtils"

const trackProgressPaths = ["/track_progress", "/track_progress/:questId/"]

class TrackProgressRoutingContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = { progressGridSectionId: "" }
  }

  componentDidMount() {
    this.fetchProgressData()
    this.fetchExpeditionData()
    this.syncStateWithRoute()
  }
  componentDidUpdate(prevState) {
    const { committedCourseIds, committedSectionIds, committedStudentIds } = this.props
    this.syncStateWithRoute()
    if (
      prevState.committedCourseIds !== committedCourseIds ||
      prevState.committedSectionIds !== committedSectionIds ||
      prevState.committedStudentIds !== committedStudentIds
    ) {
      this.fetchExpeditionData()
    }
  }

  syncStateWithRoute() {
    const { match, dispatch, moduleId, sequenceId, questId } = this.props
    const matchedModuleId = match.params.moduleId
    const matchedSequenceId = match.params.sequenceId
    const matchedQuestId = match.params.questId

    // Set quest id
    if (matchedQuestId) {
      executeIfNotEqual(questId, matchedQuestId, () => {
        this.props.dispatch(setQuestId(matchedQuestId))
        this.fetchProgressData()
      })
    } else {
      executeIfNotEqual(questId, "", () => {
        this.props.dispatch(setQuestId(""))
        this.fetchProgressData()
      })
    }

    if (this.props.isRouting) {
      this.props.dispatch(setRoutingStatus(false))
    }
  }

  commitProgressOverviewFilters = () => this.props.dispatch(commitSelectedProgressOverviewFilters())

  revertProgressOverviewFilters = () => this.props.dispatch(revertSelectedProgressOverviewFilters())

  setProgressOverviewShowAllContent = val =>
    this.props.dispatch(selectProgressOverviewShowAllContent(val))

  toggleProgressOverviewModuleId = id =>
    this.props.dispatch(
      selectProgressOverviewModuleIds(
        toggleValueInList(parseInt(id), this.props.selectedProgressOverviewModuleIds)
      )
    )

  toggleProgressOverviewContentType = type =>
    this.props.dispatch(
      selectProgressOverviewContentTypes(
        toggleValueInList(type, this.props.selectedProgressOverviewContentTypes)
      )
    )

  setNgssDimensionsShowAllContent = val =>
    this.props.dispatch(selectNgssDimensionsShowAllContent(val))

  setNgssDimensionsContentType = val => this.props.dispatch(selectNgssDimensionsContentType(val))

  setPerformanceExpectationsShowAllContent = val =>
    this.props.dispatch(selectPerformanceExpectationsShowAllContent(val))

  togglePerformanceExpectationsModuleId = id =>
    this.props.dispatch(
      selectPerformanceExpectationsModuleIds(
        toggleValueInList(parseInt(id), this.props.performanceExpectationsModuleIds)
      )
    )

  // ==============================================================================================================================================
  // Apply ONLY PRIMARY filters to the given data in the object. Each item gets augmented with extra attributes like names in addition
  // to IDs in order to make filtering easier.
  //
  // Input is expected to be an object of type { id1: data1, id2: data2, ... }. The data objects need to have the following keys in order
  // to be filtered. Filtering QuestProgress and WorkProductSummary objects are the intended use case and should already implement these keys:
  // - quest_id
  // - module_id
  // - student_id and/or user_uuid
  // - section_id
  // - course_id
  // - to_be_assigned_at or assigned_at
  // - completed_at
  //
  // Optionally, the data objects may implement the following keys:
  // - sequence_id
  // ==============================================================================================================================================
  getPrimaryFilteredObjects = object => {
    if (this.props.isRouting) {
      return []
    }

    // Add some attributes to each assignment that will make them easier to filter.
    // Added attributes use underscore convention to match what the JSONAPI redux plugin creates.
    let augmentedObject = Object.assign({}, object)
    // Remove sandbox assignments
    _.remove(augmentedObject, objectData => _.isEmpty(objectData.quest_id))

    let foundSequence, foundModule, foundQuest, foundStudent, foundSection, foundCourse
    _.each(augmentedObject, a => {
      // Add quest name for display purposes
      foundQuest = _.find(this.props.quests, q => q.id == a.quest_id) || {}
      a.quest_name = foundQuest.name

      // Add module name and id for display and filtering, respectively
      foundModule = _.find(this.props.modules, m => m.id == foundQuest.module_id) || {}
      a.module_name = foundModule.name
      a.module_id = parseInt(foundModule.id) || undefined

      // Add sequence id and name for display and filtering, respectively.
      // This is done regardless of whether the assignment was created as part of a sequence
      foundSequence =
        _.find(this.props.sequences, s =>
          _.map(s.quests, q => parseInt(q.id)).includes(parseInt(a.quest_id))
        ) || {}
      a.order_in_sequence = _.isEmpty(foundSequence)
        ? undefined
        : foundSequence.quest_order.indexOf(parseInt(a.quest_id)) + 1
      a.sequence_title = foundSequence.title
      a.sequence_id = parseInt(foundSequence.id) || undefined

      a.ngss_cccs = foundQuest.ngss_cccs
      a.ngss_dcis = foundQuest.ngss_dcis
      a.ngss_seps = foundQuest.ngss_seps
      a.ngss_pes = foundQuest.ngss_pes

      // Add quest type status for filtering
      if (a.sequence_id) {
        a.quest_type = "Storyline"
      } else if (foundQuest.ngss_enrichment) {
        a.quest_type = "Enrichment"
      } else {
        a.quest_type = "Practice"
      }

      // TODO: this filtering function needs to be able to find _all_ students associated with a user, not just one
      // Add class attributes if the progress is an assignment
      foundStudent =
        _.find(
          this.props.students,
          student =>
            parseInt(student.id) === a.student_id ||
            student.profile_uuid === a.user_uuid ||
            student.profile_uuid === a.profile_uuid
        ) || {}
      foundSection =
        _.find(this.props.sections, section => parseInt(section.id) === foundStudent.section_id) ||
        {}
      foundCourse =
        _.find(this.props.courses, course => parseInt(course.id) === foundSection.course_id) || {}
      a.section_id = parseInt(foundSection.id) || undefined
      a.course_id = parseInt(foundCourse.id) || undefined
    })

    // Apply the primary filters to the progress collection
    const {
      committedCourseIds,
      committedSectionIds,
      committedStudentIds,
      committedUserUuids,
      committedStartingDate,
      committedEndingDate
    } = this.props
    if (!_.isEmpty(committedStudentIds) || !_.isEmpty(committedUserUuids)) {
      augmentedObject = _.filter(
        augmentedObject,
        progress =>
          committedStudentIds.includes(progress.student_id) ||
          committedUserUuids.includes(progress.user_uuid) ||
          committedUserUuids.includes(progress.profile_uuid)
      )
    }

    if (!_.isEmpty(committedSectionIds)) {
      augmentedObject = _.filter(augmentedObject, progress =>
        committedSectionIds.includes(progress.section_id)
      )
    }
    if (!_.isEmpty(committedCourseIds)) {
      augmentedObject = _.filter(augmentedObject, progress =>
        committedCourseIds.includes(progress.course_id)
      )
    }
    if (committedStartingDate) {
      augmentedObject = _.filter(augmentedObject, progress => {
        return (
          progress.to_be_assigned_at >= committedStartingDate ||
          progress.assigned_at >= committedStartingDate ||
          progress.started_at >= committedStartingDate ||
          progress.completed_at >= committedStartingDate
        )
      })
    }
    if (committedEndingDate) {
      augmentedObject = _.filter(augmentedObject, progress => {
        return (
          progress.to_be_assigned_at <= committedEndingDate ||
          progress.assigned_at <= committedEndingDate ||
          progress.started_at <= committedEndingDate ||
          progress.completed_at <= committedEndingDate
        )
      })
    }

    return augmentedObject
  }

  // ========================================
  // Students filtered by the primary filters
  // ========================================
  getFilteredStudents = () => {
    let augmentedStudents = Object.assign({}, this.props.students)
    let foundSection
    _.each(augmentedStudents, student => {
      student.id = parseInt(student.id)
      student.user_uuid = student.profile_uuid
      foundSection = _.find(
        this.props.sections,
        section => parseInt(section.id) === parseInt(student.section_id)
      )
      student.section_id = parseInt(foundSection.id)
      student.course_id = parseInt(
        _.find(
          this.props.courses,
          course => parseInt(course.id) === parseInt(foundSection.course_id)
        ).id
      )
    })

    const { committedCourseIds, committedSectionIds, committedStudentIds } = this.props
    if (!_.isEmpty(committedStudentIds)) {
      augmentedStudents = _.filter(augmentedStudents, student =>
        committedStudentIds.includes(student.id)
      )
    }
    if (!_.isEmpty(committedSectionIds)) {
      augmentedStudents = _.filter(augmentedStudents, student =>
        committedSectionIds.includes(student.section_id)
      )
    }
    if (!_.isEmpty(committedCourseIds)) {
      augmentedStudents = _.filter(augmentedStudents, student =>
        committedCourseIds.includes(student.course_id)
      )
    }

    // Turn single object into an array of students
    return _.map(augmentedStudents, (obj, id) => obj)
  }

  getNameForUserUuid = uuid => {
    if (_.isEmpty(this.props.students)) {
      return ""
    }
    const foundProfile = _.find(
      this.props.students,
      student => student.profile_uuid === uuid
    ).profile

    if (foundProfile.last_name) {
      return foundProfile.first_name + " " + foundProfile.last_name
    } else {
      return foundProfile.first_name
    }
  }

  getFilteredWorkProductSubmissions = (students, workProductSummaries, progresses) => {
    const {
      questId,
      quests,
      flowchartSubmissions,
      researchSubmissions,
      argumentBuilderSubmissions,
      categorizationSubmissions,
      pictureClickSubmissions,
      heredityProbabilitySubmissions,
      frequencyChartSubmissions,
      committedStartingDate,
      committedEndingDate
    } = this.props
    const studentUuids = _.map(students, s => s.user_uuid)
    // Fuction for applying filters to each WP submission type
    const applyFilters = submissions => {
      // Filter by student grouping
      let filteredSubmissions = _.filter(submissions, submission =>
        studentUuids.includes(submission.user_uuid)
      )
      // Filter by date
      if (committedStartingDate) {
        filteredSubmissions = _.filter(
          filteredSubmissions,
          submission => submission.selection_played_at >= committedStartingDate
        )
      }
      if (committedEndingDate) {
        filteredSubmissions = _.filter(
          filteredSubmissions,
          submission => submission.selection_played_at <= committedEndingDate
        )
      }
      // Filter by quest id
      if (questId) {
        filteredSubmissions = _.filter(
          filteredSubmissions,
          submission => submission.quest_id == questId
        )
      }

      // Only show submissions that belong to completed progress
      filteredSubmissions = _.filter(
        filteredSubmissions,
        sub => (_.find(progresses, prog => prog.id === sub.progress_uuid) || {}).completed_at
      )

      return filteredSubmissions
    }

    // Grouping each WP submission type into different tasks, delineated by their stage order.
    // This works assuming that multiple WP tasks of the same type don't exist in the same stage.
    // Otherwise, task_uuid will need to be added to properly group them.
    const groupByTask = submissions => _.groupBy(submissions, submission => submission.stage_order)

    let workProductSubmissions = {
      Flowchart: groupByTask(applyFilters(flowchartSubmissions)),
      Research: groupByTask(applyFilters(researchSubmissions)),
      "Argument Builder": groupByTask(applyFilters(argumentBuilderSubmissions)),
      Categorization: groupByTask(applyFilters(categorizationSubmissions)),
      "Picture Click": groupByTask(applyFilters(pictureClickSubmissions)),
      "Heredity Probability": groupByTask(applyFilters(heredityProbabilitySubmissions)),
      "Frequency Chart": groupByTask(applyFilters(frequencyChartSubmissions))
    }

    const processFlowchartSubmissions = (submissions, gradedStudents) => {
      const groupedSubmissions = _.groupBy(submissions, submission => submission.submission_hash)
      const uniqueStudentCount = parseFloat(gradedStudents.length)
      let currentStudents

      return {
        description: submissions[0].task_description,
        flowcharts: _.orderBy(
          _.map(groupedSubmissions, (gsubmissions, hash) => {
            currentStudents = _.filter(gradedStudents, s =>
              _.map(gsubmissions, "user_uuid").includes(s.uuid)
            )

            return {
              edges: _.map(gsubmissions[0].edge_collection, edge => ({
                toId: edge.to_id,
                fromId: edge.from_id,
                isCorrect: edge.is_correct
              })),
              nodes: _.map(gsubmissions[0].node_collection, node => ({
                id: node.id,
                name: node.title,
                imgLink: "https://tyto-game-assets.s3.amazonaws.com/" + node.image_url
              })),
              students: _.map(currentStudents, s => ({
                name: s.name,
                quality: s.quality
              })),
              isCorrect: gsubmissions[0].passed,
              percentage: currentStudents.length / uniqueStudentCount
            }
          }),
          "isCorrect",
          "desc"
        )
      }
    }

    const processResearchSubmissions = (submissions, gradedStudents) => {
      //grouped by article
      const groupedSubmissions = _.groupBy(submissions, submission => submission.source_title)
      const uniqueStudentCount = parseFloat(_.uniqBy(submissions, "user_uuid").length)
      let selectedCitations,
        foundProfile,
        studentsWithCitation,
        articleGradingsWithSelectedCitation,
        quality

      return {
        description: submissions[0].task_description,
        articles: _.map(groupedSubmissions, (articleGradings, title) => {
          // Citations that were either selected and/or correct need to be displayed.
          // Citations should only appear at most twice, once per its selected state
          selectedCitations = _.uniqBy(
            _.filter(
              _.flatMap(articleGradings, grading => grading.citations),
              citation => citation.is_selected_for_use || citation.is_correct
            ),
            c => c.is_selected_for_use + c.citation_uuid
          )

          return {
            author: articleGradings[0].source_author,
            title: articleGradings[0].source_title,
            text: articleGradings[0].source_text,
            answers: _.orderBy(
              _.map(selectedCitations, citation => {
                articleGradingsWithSelectedCitation = _.filter(articleGradings, grading =>
                  _.map(
                    _.filter(
                      grading.citations,
                      c => c.is_selected_for_use || (!c.is_selected_for_use && c.is_correct)
                    ),
                    c => c.citation_uuid
                  ).includes(citation.citation_uuid)
                )
                studentsWithCitation = _.filter(gradedStudents, s =>
                  _.map(articleGradingsWithSelectedCitation, "user_uuid").includes(s.uuid)
                )

                if (citation.is_correct) {
                  quality = citation.is_selected_for_use ? "correct" : "neutral"
                } else {
                  quality = "incorrect"
                }

                return {
                  uuid: citation.citation_uuid,
                  students: studentsWithCitation,
                  percentage:
                    quality === "correct"
                      ? undefined
                      : studentsWithCitation.length / uniqueStudentCount, //correct percentage is always 100%
                  type: quality,
                  title: quality === "neutral" ? "Missing" : quality.toUpperCase(),
                  text: citation.citation_text
                }
              }),
              ["type", "text"],
              ["asc", "desc"]
            )
          }
        })
      }
    }

    const processArgumentBuilderSubmissions = (submissions, gradedStudents) => {
      //grouped by argument
      const questionGroupedSubmissions = _.groupBy(submissions, sub => sub.component_question)
      const claimGroupedSubmissions = _.groupBy(submissions, sub => sub.chosen_claim)
      const uniqueStudentUuids = _.map(gradedStudents, "uuid")
      let selectedStudentUuids, selectedStudents, quality

      const getQuality = (argCorrect, claimCorrect, points) => {
        if (claimCorrect && argCorrect) {
          return points > 0 ? "correct" : "neutral"
        } else {
          return argCorrect ? "neutral" : "incorrect"
        }
      }

      return {
        description: submissions[0].primary_question,
        students: _.map(gradedStudents, s => ({
          name: s.name,
          quality: s.quality
        })),
        claims: _.orderBy(
          _.map(claimGroupedSubmissions, (subs, claim) => {
            selectedStudents = _.filter(gradedStudents, s =>
              _.map(subs, "user_uuid").includes(s.uuid)
            )

            return {
              text: claim,
              isCorrect: subs[0].is_chosen_claim_correct,
              percentage: selectedStudents.length / parseFloat(uniqueStudentUuids.length)
            }
          }),
          "isCorrect",
          "desc"
        ),
        questions: _.map(questionGroupedSubmissions, (qsubs, question) => ({
          question_text: qsubs[0].component_question,
          arguments: _.orderBy(
            _.map(_.groupBy(qsubs, "argument_fingerprint"), (asubs, fingerprint) => {
              selectedStudentUuids = _.map(_.uniqBy(asubs, "user_uuid"), "user_uuid")
              quality = getQuality(
                asubs[0].is_argument_correct,
                asubs[0].is_chosen_claim_correct,
                asubs[0].argument_points
              )

              return {
                type: quality,
                title: quality.toUpperCase() + " Argument",
                subtitle:
                  asubs[0].is_argument_correct && !asubs[0].is_chosen_claim_correct
                    ? "Correct, but for an incorrect claim"
                    : undefined,
                text: asubs[0].first_evidence_text,
                firstBridge: asubs[0].first_bridge_statement,
                secondText: asubs[0].second_evidence_text,
                secondBridge: asubs[0].second_bridge_statement,
                thirdText: asubs[0].third_evidence_text,
                students: _.map(selectedStudentUuids, uuid => ({
                  name: this.getNameForUserUuid(uuid)
                })),
                percentage: selectedStudentUuids.length / parseFloat(uniqueStudentUuids.length)
              }
            }),
            ["type", "percentage"],
            ["asc", "desc"]
          )
        }))
      }
    }

    const processCategorizationSubmissions = (submissions, gradedStudents) => {
      const groupedSubmissions = _.groupBy(submissions, sub => sub.statement)
      const uniqueStudentUuids = _.map(gradedStudents, "uuid")
      let selectedStudents, answerGroupedSubs

      return {
        description: submissions[0].task_description,
        submissions: _.map(groupedSubmissions, (subs, statement) => {
          answerGroupedSubs = _.groupBy(subs, "selected_category")

          return {
            statement,
            answers: _.orderBy(
              _.map(answerGroupedSubs, (asubs, answer) => {
                selectedStudents = _.filter(gradedStudents, s =>
                  _.map(asubs, "user_uuid").includes(s.uuid)
                )

                return {
                  selection: answer,
                  isCorrect: asubs[0].actual_category === answer,
                  students: _.map(selectedStudents, s => ({
                    name: s.name,
                    quality: s.quality
                  })),
                  percentage: selectedStudents.length / parseFloat(uniqueStudentUuids.length)
                }
              }),
              "isCorrect",
              "desc"
            )
          }
        })
      }
    }

    const processHeredityProbabilitySubmissions = (submissions, gradedStudents) => {
      //grouped by answer set
      const groupedSubmissions = _.groupBy(submissions, sub => sub.submission_hash)
      const uniqueStudentUuids = _.map(_.uniqBy(submissions, "user_uuid"), "user_uuid")
      const totalCombinations = Math.pow(submissions[0].parent_one_genes.length, 2)
      let selectedStudents

      return {
        description: submissions[0].task_description,
        geneKey: submissions[0].gene_key,
        parentGenes: [submissions[0].parent_one_genes, submissions[0].parent_two_genes],
        submissions: _.orderBy(
          _.map(groupedSubmissions, (subs, hash) => {
            selectedStudents = _.filter(gradedStudents, s =>
              _.map(subs, "user_uuid").includes(s.uuid)
            )

            return {
              isCorrect: subs[0].all_correct,
              students: selectedStudents,
              answers: _.map(subs[0].submission_choices, choice => ({
                question: choice.label,
                givenAnswer: choice.entered_value,
                denominator: totalCombinations,
                isCorrect: choice.entered_value === choice.expected_value
              }))
            }
          }),
          "isCorrect",
          "desc"
        )
      }
    }

    const processFrequencyChartSubmissions = (submissions, gradedStudents) => {
      const {
        task_uuid,
        task_description,
        activity_mode,
        chart_title,
        data_table_x_item_name_header,
        data_table_x_value_header,
        x_axis_title,
        x_axis_unit_name,
        x_axis_max,
        x_axis_min,
        x_axis_tick_interval,
        y_axis_unit_name,
        y_axis_tick_interval,
        y_axis_title,
        y_axis_max,
        y_axis_min
      } = submissions[0]

      const groupedByStudent = _.groupBy(submissions, submission => submission.user_uuid)
      let correctAnswer = {}
      Object.values(gradedStudents).forEach(student => {
        const answers = groupedByStudent[student.uuid] || []
        answers.forEach(answer => {
          if (_.isEmpty(correctAnswer) && answer.is_correct) {
            correctAnswer = JSON.parse(answer.submission_answers)
          }
        })
      })

      return {
        taskUuid: task_uuid,
        description: task_description,
        taskMode: activity_mode,
        chartDescription: {
          chartTitle: chart_title,
          xItemNameHeader: data_table_x_item_name_header,
          xValueHeader: data_table_x_value_header,
          xAxis: {
            title: x_axis_title,
            unitName: x_axis_unit_name,
            min: x_axis_min,
            max: x_axis_max,
            interval: x_axis_tick_interval
          },
          yAxis: {
            title: y_axis_title,
            unitName: y_axis_unit_name,
            min: y_axis_min,
            max: y_axis_max,
            interval: y_axis_tick_interval
          }
        },
        correctAnswer: correctAnswer,
        wrongAnswers: getPopularIncorrect(groupedByStudent, "Frequency Chart"),
        gradedStudents
      }
    }

    const processPictureClickSubmissions = (submissions, gradedStudents) => {}

    const processSubmissions = {
      Flowchart: processFlowchartSubmissions,
      Research: processResearchSubmissions,
      "Argument Builder": processArgumentBuilderSubmissions,
      Categorization: processCategorizationSubmissions,
      "Picture Click": processPictureClickSubmissions,
      "Heredity Probability": processHeredityProbabilitySubmissions,
      "Frequency Chart": processFrequencyChartSubmissions
    }

    let summariesForSubmissions,
      greatStrugglePercentage,
      strugglePercentage,
      noStrugglePercentage,
      uniqueStudentUuids,
      studentCount,
      gradedStudents

    const existingWorkProductSubmissions = _.orderBy(
      _.flatMap(workProductSubmissions, (submissionsByTask, type) =>
        _.map(submissionsByTask, (submissions, stageOrder) => {
          summariesForSubmissions = _.filter(
            workProductSummaries,
            summ => submissions[0].task_uuid === summ.task_uuid
          )
          const buckets = _.map(gradeSummariesPerStudent(summariesForSubmissions), gr =>
            Object.assign({ name: this.getNameForUserUuid(gr.uuid) }, gr)
          )
          studentCount = parseFloat(students.length)

          greatStrugglePercentage = _.filter(buckets, s => s.bucket === 1).length / studentCount
          strugglePercentage = _.filter(buckets, s => s.bucket === 2).length / studentCount
          noStrugglePercentage = _.filter(buckets, s => s.bucket === 3).length / studentCount

          gradedStudents = _.map(buckets, b => ({
            uuid: b.uuid,
            name: b.name,
            quality: b.bucket
          }))
          return {
            type,
            order: parseInt(stageOrder),
            percentages: {
              incompletePercentage:
                1 - (greatStrugglePercentage + strugglePercentage + noStrugglePercentage),
              greatStrugglePercentage,
              strugglePercentage,
              noStrugglePercentage
            },
            submissionData: processSubmissions[type](submissions, gradedStudents)
          }
        })
      ),
      "order"
    )

    const quest = _.find(quests, quest => parseInt(quest.id) === parseInt(questId)) || {}
    const workProductTypes = _.reject(
      _.flatMap(
        _.reject(quest.work_product_progression, p => _.isEmpty(p.types)),
        stage => stage.types
      ),
      a => "Picture Click" === a
    )
    return _.map(workProductTypes, (type, i) => {
      if (_.isEmpty(existingWorkProductSubmissions[i])) {
        return {
          type,
          order: undefined, //not necessary to add, but the key should be present
          percentages: {
            incompletePercentage: 1,
            greatStrugglePercentage: 0,
            strugglePercentage: 0,
            noStrugglePercentage: 0
          },
          submissionData: []
        }
      } else {
        return existingWorkProductSubmissions[i]
      }
    })
  }

  applyProgressOverviewFilters = progresses => {
    // Apply widget-specific filters
    const {
      committedProgressOverviewModuleIds,
      committedProgressOverviewContentTypes,
      committedProgressOverviewShowAllContent
    } = this.props
    let filteredProgresses = Object.assign({}, progresses)
    if (!_.isEmpty(committedProgressOverviewContentTypes)) {
      filteredProgresses = _.filter(filteredProgresses, progress =>
        committedProgressOverviewContentTypes.includes(progress.quest_type)
      )
    }
    if (!committedProgressOverviewShowAllContent) {
      filteredProgresses = _.filter(filteredProgresses, progress => progress.to_be_assigned_at)
    }
    if (!_.isEmpty(committedProgressOverviewModuleIds)) {
      filteredProgresses = _.filter(filteredProgresses, progress =>
        committedProgressOverviewModuleIds.includes(parseInt(progress.module_id))
      )
    }

    return filteredProgresses
  }

  // ====================================================================================
  // Quest progress filtered and formatted for displaying on the Progress Overview widget
  // ====================================================================================
  groupedQuestProgresses = (progresses, filteredStudents) => {
    // Converts a single quest into JSON
    const formatQuests = (givenQuests, subtitle = undefined) =>
      _.map(givenQuests, q => ({
        title: (_.find(this.props.modules, m => m.id == q.module_id) || {}).name,
        subtitle: subtitle || q.name,
        id: q.id
      }))
    const groupQuestsByModule = quests => {
      let groupedQuests = _.groupBy(quests, quest => quest.module_id)
      groupedQuests = _.mapKeys(
        groupedQuests,
        (questList, moduleId) =>
          (_.find(this.props.modules, module => module.id === moduleId) || {}).name
      )
      return _.mapValues(groupedQuests, (quests, moduleName) => formatQuests(quests))
    }

    // exclude quests that aren't assignable
    const assignableQuests = _.filter(this.props.quests, quest => quest.assignable_via_quest_card)
    const progressQuestIds = _.uniq(_.compact(_.map(progresses, p => parseInt(p.quest_id))))

    // Sort all quests into either storylines, practice, or enrichment categories
    let sequences = _.map(this.props.sequences, s => ({
      title: (_.find(this.props.modules, m => m.id == s.module_id) || {}).name,
      subtitle: s.title,
      quests: formatQuests(s.quests)
    }))
    const sequenceQuestIds = _.flatMap(sequences, seq => _.map(seq.quests, quest => quest.id))
    const nonSequenceQuests = _.filter(
      assignableQuests,
      quest => !sequenceQuestIds.includes(quest.id)
    )
    let enrichmentQuests = _.filter(
      nonSequenceQuests,
      quest => quest.ngss_enrichment && progressQuestIds.includes(parseInt(quest.id))
    )
    let practiceQuests = _.filter(
      nonSequenceQuests,
      quest => !quest.ngss_enrichment && progressQuestIds.includes(parseInt(quest.id))
    )
    let groupedEnrichmentQuests = groupQuestsByModule(enrichmentQuests)
    let groupedPracticeQuests = groupQuestsByModule(practiceQuests)

    const formattedEnrichmentQuests =
      _.map(groupedEnrichmentQuests, (quests, moduleName) => ({
        title: moduleName,
        subtitle: "Enrichment",
        quests
      })) || []
    const formattedPracticeQuests =
      _.map(groupedPracticeQuests, (quests, moduleName) => ({
        title: moduleName,
        subtitle: "Practice",
        quests
      })) || []
    let formattedContentWithoutProgresses = sequences
      .concat(formattedEnrichmentQuests)
      .concat(formattedPracticeQuests)

    let foundProgresses, foundSequence
    // A count of all filtered students is needed to calculate percentage progress for each quest
    _.each(formattedContentWithoutProgresses, row => {
      row.progressCount = 0

      _.each(row.quests, quest => {
        quest.progresses = []
        foundSequence = _.find(this.props.sequences, s =>
          _.map(s.quests, q => parseInt(q.id)).includes(parseInt(quest.id))
        )
        if (!_.isEmpty(foundSequence)) {
          quest.order_in_sequence = foundSequence.quest_order.indexOf(parseInt(quest.id)) + 1
          row.isSequence = true
        }

        // Find all progress items for the current quest
        foundProgresses = _.filter(progresses, progress => progress.quest_id === parseInt(quest.id))
        if (!_.isEmpty(foundProgresses)) {
          quest.progresses = foundProgresses
          row.progressCount += foundProgresses.length
        }

        quest.completionPercentage =
          _.filter(foundProgresses, p => !!p.completed_at).length /
            parseFloat(filteredStudents.length) || 0
      })

      row.quests = _.orderBy(row.quests, quest => quest.order_in_sequence)

      // Select the most recent timestamp from the relevant progesses for sorting later
      row.timestamp = _.max(
        _.map(
          _.flatMap(row.quests, quest => quest.progresses),
          progress => progress.to_be_assigned_at || progress.completed_at
        )
      )
      // The percentage of a row is equal to the average percentage of the component quests
      row.completionPercentage =
        _.sum(_.map(row.quests, quest => quest.completionPercentage)) /
          parseFloat(row.quests.length) || 0
    })

    // Order the remaining content by timestamp and remove rows that have no completed progress
    const orderedProgress = _.orderBy(
      _.filter(formattedContentWithoutProgresses, progress => progress.progressCount > 0),
      progress => progress.timestamp
    )

    return orderedProgress
  }

  // ==============================================================================================================================
  // All three dimensions with their component items filtered and grouped for displaying on the Three Dimensions of the NGSS widget
  // ==============================================================================================================================
  groupedNgssDimensionData = (completedProgresses, filteredStudents) => {
    if (
      _.isEmpty(this.props.ngssCccs) ||
      _.isEmpty(this.props.ngssDcis) ||
      _.isEmpty(this.props.ngssSeps)
    ) {
      return []
    }

    // Sort progresses into a collection of their associated dimension item.
    // dimensions should be a list of item names ("Patterns", "Developing and Using Models", etc)
    // type should be 'ccc', 'dci', or 'sep'
    const groupProgressesByDimension = (dimensions, type) => {
      let collection = {}
      const keyName = `ngss_${type}s`
      _.each(Object.keys(dimensions), dimensionName => {
        collection[dimensionName] = _.filter(completedProgresses, progress =>
          _.isEmpty(progress[keyName])
            ? null
            : Object.keys(progress[keyName]).includes(dimensionName)
        )
      })
      return collection
    }

    // For each dimension, count how many progresses have been completed for that dimension per student.
    // Returns
    const getDimensionCountsPerStudent = collection =>
      _.mapValues(collection, (progresses, dimensionName) =>
        Object.values(
          _.mapValues(
            _.groupBy(progresses, progress => progress.student_id),
            (progresses, studentId) => progresses.length
          )
        )
      )

    const groupedCccs = getDimensionCountsPerStudent(
      groupProgressesByDimension(this.props.ngssCccs, "ccc")
    )
    const groupedDcis = getDimensionCountsPerStudent(
      groupProgressesByDimension(this.props.ngssDcis, "dci")
    )
    const groupedSeps = getDimensionCountsPerStudent(
      groupProgressesByDimension(this.props.ngssSeps, "sep")
    )

    const getPercentagessFromGroupedDimensions = (dimensions, studentCount, maxCount) =>
      _.mapValues(dimensions, (counts, name) => {
        const average = (_.sum(counts) || 0) / parseFloat(studentCount)
        return average / parseFloat(maxCount) || 0
      })

    let maxCccCount = _.max(_.flatMap(groupedCccs, (counts, name) => counts))
    let maxDciCount = _.max(_.flatMap(groupedDcis, (counts, name) => counts))
    let maxSepCount = _.max(_.flatMap(groupedSeps, (counts, name) => counts))
    let cccPercentages = getPercentagessFromGroupedDimensions(
      groupedCccs,
      filteredStudents.length,
      maxCccCount
    )
    let dciPercentages = getPercentagessFromGroupedDimensions(
      groupedDcis,
      filteredStudents.length,
      maxDciCount
    )
    let sepPercentages = getPercentagessFromGroupedDimensions(
      groupedSeps,
      filteredStudents.length,
      maxSepCount
    )

    const orderPercentageList = list => _.orderBy(list, item => item.percentage, ["desc"])

    let cccPercentageObject = {
      "Cross-Cutting Concepts (CCCs)": orderPercentageList(
        _.map(cccPercentages, (percentage, name) => ({
          type: "ccc",
          name,
          percentage
        }))
      )
    }
    let dciPercentageObject = {
      "Disciplinary Core Ideas (DCIs)": orderPercentageList(
        _.map(dciPercentages, (percentage, name) => ({
          type: "dci",
          name,
          percentage
        }))
      )
    }
    let sepPercentageObject = {
      "Science and Engineering Practices (SEPs)": orderPercentageList(
        _.map(sepPercentages, (percentage, name) => ({
          type: "sep",
          name,
          percentage
        }))
      )
    }

    const dimensionType = this.props.ngssDimensionsContentType
    if (dimensionType === 1) {
      return Object.assign(sepPercentageObject, cccPercentageObject, dciPercentageObject)
    } else if (dimensionType === 2) {
      return cccPercentageObject
    } else if (dimensionType === 3) {
      return dciPercentageObject
    } else {
      return sepPercentageObject
    }
  }

  groupedPerformanceExpenctationData = (workProductSummaries, students) => {
    const studentCount = parseFloat(students.length)
    let summaries = Object.values(workProductSummaries)

    // Filter out WP types that aren't currently supported
    const excludedWorkProductTypes = ["Picture Click"]
    summaries = _.filter(summaries, s => !excludedWorkProductTypes.includes(s.work_product_type))

    const { quests, performanceExpectationsShowAllContent, performanceExpectationsModuleIds } =
      this.props
    // Filter the current list with the widget-specific filters
    summaries = _.filter(summaries, summary =>
      performanceExpectationsShowAllContent
        ? true
        : !_.isEmpty(summary.to_be_assigned_at || summary.assigned_at)
    )
    const filteredQuests = _.isEmpty(performanceExpectationsModuleIds)
      ? quests
      : _.filter(quests, q => performanceExpectationsModuleIds.includes(parseInt(q.module_id)))

    const allPes = _.uniqBy(_.flatMap(filteredQuests, "ngss_pes"), "code")
    const allPeCodes = _.compact(_.uniq(_.map(allPes, "code")))
    let groupedSummaries = {}
    let foundSummaries

    // Add a list of PE codes to each summary item to make them easier to sort later
    _.each(summaries, summ => (summ.ngss_pe_codes = _.map(summ.ngss_pes, "code")))

    _.each(allPeCodes, code => {
      foundSummaries = _.filter(summaries, summ => summ.ngss_pe_codes.includes(code))
      groupedSummaries[code] = gradeSummariesPerStudent(foundSummaries) || []
    })

    // Accepts a group of dimensions in the form [{ title: [clause1, clause2, ...] }, ...]
    // and returns them in the form [{ name: "title", clauses: [clause1, clause2, ...] }, ...]
    const formatDimensionGroup = group =>
      _.map(group, item => ({
        name: Object.keys(item)[0],
        clauses: Object.values(item)[0]
      }))

    let greatStruggle, struggle, noStruggle, fullPE
    groupedSummaries = _.map(groupedSummaries, (buckets, code) => {
      greatStruggle = _.filter(buckets, s => s.bucket === 1).length / studentCount
      struggle = _.filter(buckets, s => s.bucket === 2).length / studentCount
      noStruggle = _.filter(buckets, s => s.bucket === 3).length / studentCount
      fullPE = _.find(allPes, pe => pe.code === code) || {}

      return {
        code,
        description: fullPE.description,
        seps: formatDimensionGroup(fullPE.seps || []),
        dcis: formatDimensionGroup(fullPE.dcis || []),
        cccs: formatDimensionGroup(fullPE.cccs || []),
        mostRecentActivityDate: _.max(
          _.map(
            _.flatMap(buckets, b => b.summaries),
            s => s.completed_at || s.started_at || s.to_be_assigned_at || s.assigned_at
          )
        ),
        percentages: {
          incomplete: 1 - (greatStruggle + struggle + noStruggle),
          greatStruggle,
          struggle,
          noStruggle
        }
      }
    })

    return _.orderBy(groupedSummaries, ["mostRecentActivityDate", "code"], ["asc", "asc"])
  }

  strugglingAreasData = (workProductSummaries, students) => {
    if (_.isEmpty(workProductSummaries)) {
      return
    }

    let summaries
    if (!_.isArray(workProductSummaries)) {
      summaries = Object.values(workProductSummaries)
    } else {
      summaries = workProductSummaries
    }

    // Not all WP types will appear in this widget
    const excludedWorkProductTypes = ["Categorization", "Picture Click"]
    const { quests, ngssCccs, ngssDcis, ngssSeps } = this.props
    const strugglingSummaries = _.filter(
      _.map(summaries, s => gradeSummary(s)),
      summary => summary.grade === 1
    )

    let groupedSummaries = {}
    let dimensionItems
    _.each(strugglingSummaries, summary => {
      _.each(summary.ngss_dcis, (clauses, dimItem) => {
        if (!excludedWorkProductTypes.includes(summary.work_product_type)) {
          groupedSummaries[dimItem] = groupedSummaries[dimItem] || {}
          groupedSummaries[dimItem][summary.work_product_type] =
            groupedSummaries[dimItem][summary.work_product_type] || []
          groupedSummaries[dimItem][summary.work_product_type].push(summary)
        }
      })
    })

    let uniqueStudentCount
    return _.flatMap(groupedSummaries, (typesObj, dimItem) =>
      _.map(typesObj, (summaries, type) => {
        // Each student should only be represented once in each task-dci bucket
        uniqueStudentCount = _.uniqBy(summaries, "user_uuid").length

        return {
          count: uniqueStudentCount,
          percentage: uniqueStudentCount / parseFloat(students.length),
          dimensionItem: dimItem,
          workProductType: type,
          quests: _.map(_.uniqBy(summaries, "quest_id"), summary => ({
            name: summary.quest_name,
            id: summary.quest_id
          }))
        }
      })
    )
  }

  fetchProgressData = () => {
    const { match, dispatch, quests, questId } = this.props
    const matchedQuestId = match.params.questId
    const quest = _.find(quests, q => q.id == questId)

    dispatch(readEndpoint("/modules"))
    dispatch(readEndpoint("/sequences?include=quests"))
    dispatch(readEndpoint("/quests"))
    dispatch(readEndpoint("/ngss-cccs"))
    dispatch(readEndpoint("/ngss-seps"))
    dispatch(readEndpoint("/ngss-dcis"))
    dispatch(readEndpoint("/quest-progresses"))
    dispatch(readEndpoint("/work-product-summaries"))
    dispatch(readEndpoint("/sections?include=students,course,students.profile"))

    if (matchedQuestId) {
      _.each(
        [
          "categorization",
          "argument-builder",
          "research",
          "flowchart",
          "heredity-probability",
          "frequency-chart"
        ],
        type =>
          dispatch(
            readEndpoint(`/${type.toLowerCase()}-submissions?filter[quest-id]=${matchedQuestId}`)
          )
      )
    }
  }

  fetchExpeditionData = () => {
    const { committedCourseIds, committedSectionIds, committedStudentIds, dispatch } = this.props
    const fetchExpeditionData = `
      query LoadExpeditionData($courseIds:[ID!], $sectionIds:[ID!],$studentIds:[ID!]){
        expeditionAssignments(courseIds: $courseIds, sectionIds:$sectionIds, studentIds:$studentIds)
      }
      `

    const vars = {
      courseIds: committedCourseIds,
      sectionIds: committedSectionIds,
      studentIds: committedStudentIds
    }

    const callbacks = {
      onComplete: json => {
        const results = json.data.expeditionAssignments
        if (!_.isEmpty(results.Items)) {
          dispatch(setExpeditionAssignments(results.Items))
        } else {
          dispatch(setExpeditionAssignments())
        }
      },
      onFailure: error => {
        console.error(error)
      }
    }

    runQuery(fetchExpeditionData, vars, callbacks)
  }

  // Doesn't use redux because 1) this is a temporary feature, and 2) this page needs to be refactored w/out redux
  setProgressGridSection = id => {
    this.setState({ progressGridSectionId: id })
  }

  render() {
    const { quests, students, questId, questProgresses, workProductSummaries } = this.props
    // Prepare data for components. First, run primary filters
    const filteredQuestProgresses = this.getPrimaryFilteredObjects(questProgresses)
    const filteredWorkProductSummaries = this.getPrimaryFilteredObjects(workProductSummaries)
    const filteredStudents = this.getFilteredStudents()
    const allStudents = Object.values(students || {}) || []
    const completedProgresses = _.filter(
      filteredQuestProgresses,
      progress => !!progress.completed_at
    )

    if (questId) {
      const quest = _.find(quests, q => q.id == questId) || {}
      const workProductTypes = (quest || {}).work_product_types || []
      const workProductData = this.getFilteredWorkProductSubmissions(
        filteredStudents,
        filteredWorkProductSummaries,
        filteredQuestProgresses
      )

      return (
        <TrackProgressQuestPage
          isLoading={this.props.readingCount > 0}
          workProductTypes={workProductTypes}
          workProductData={workProductData}
          questName={quest.name}
        />
      )
    } else {
      // Run each components' individual filters
      const progressOverviewProgresses = this.applyProgressOverviewFilters(
        Object.values(filteredQuestProgresses)
      )

      // Get data for each component
      const progressOverviewData =
        this.groupedQuestProgresses(progressOverviewProgresses, filteredStudents) || []
      const ngssDimensionData = this.groupedNgssDimensionData(completedProgresses, filteredStudents)
      const performanceExpectationData = this.groupedPerformanceExpenctationData(
        filteredWorkProductSummaries,
        filteredStudents
      )
      const strugglingAreasDisplayData =
        this.strugglingAreasData(filteredWorkProductSummaries, filteredStudents) || []

      // Data to help with displaying empty states
      const unfilteredStrugglingAreaData =
        this.strugglingAreasData(Object.values(workProductSummaries || {}), allStudents) || []
      const unfilteredProgressOverviewData =
        this.groupedQuestProgresses(Object.values(questProgresses || {}), allStudents) || []
      const anyAssignments = _.filter(questProgresses, p => p.to_be_assigned_at).length > 0
      const anyFilteredStrugglingAreas =
        unfilteredStrugglingAreaData.length > strugglingAreasDisplayData.length
      const anyFilteredProgresses =
        unfilteredProgressOverviewData.length > progressOverviewData.length
      const groupedExpeditionAssignments = _.orderBy(
        this.props.expeditionAssignments,
        ["expedition_instances[0].assigned_at"],
        ["desc"]
      )

      return (
        <TrackProgressPage
          isLoading={this.props.readingCount > 0}
          progressData={progressOverviewData}
          ngssDimensionData={ngssDimensionData}
          performanceExpectationData={performanceExpectationData}
          strugglingAreasData={strugglingAreasDisplayData}
          selectedProgressOverviewShowAllContent={this.props.selectedProgressOverviewShowAllContent}
          selectedProgressOverviewModuleIds={this.props.selectedProgressOverviewModuleIds}
          selectedProgressOverviewContentTypes={this.props.selectedProgressOverviewContentTypes}
          committedProgressOverviewShowAllContent={
            this.props.committedProgressOverviewShowAllContent
          }
          committedProgressOverviewModuleIds={this.props.committedProgressOverviewModuleIds}
          committedPogressOverviewContentTypes={this.props.committedProgressOverviewContentTypes}
          setProgressOverviewShowAllContent={this.setProgressOverviewShowAllContent}
          toggleProgressOverviewModuleId={this.toggleProgressOverviewModuleId}
          toggleProgressOverviewContentType={this.toggleProgressOverviewContentType}
          commitProgressOverviewFilters={this.commitProgressOverviewFilters}
          revertProgressOverviewFilters={this.revertProgressOverviewFilters}
          ngssDimensionsShowAllContent={this.props.ngssDimensionsShowAllContent}
          ngssDimensionsContentType={this.props.ngssDimensionsContentType}
          setNgssDimensionsShowAllContent={this.setNgssDimensionsShowAllContent}
          setNgssDimensionsContentType={this.setNgssDimensionsContentType}
          performanceExpectationsShowAllContent={this.props.performanceExpectationsShowAllContent}
          performanceExpectationsModuleIds={this.props.performanceExpectationsModuleIds}
          setPerformanceExpectationsShowAllContent={this.setPerformanceExpectationsShowAllContent}
          togglePerformanceExpectationsModuleId={this.togglePerformanceExpectationsModuleId}
          modules={this.props.modules}
          sectionCollection={_.map(this.props.sections, s => ({
            value: s.id,
            label: s.name
          }))}
          selectedProgressGridSectionId={this.state.progressGridSectionId}
          handleProgressGridSectionChange={this.setProgressGridSection}
          anyProgresses={(Object.values(questProgresses || {}) || []).length > 0}
          anyCompletedProgresses={completedProgresses.length > 0}
          anyFilteredStrugglingAreas={anyFilteredStrugglingAreas}
          anyFilteredProgresses={anyFilteredProgresses}
          quests={this.props.quests}
          expeditionAssignments={groupedExpeditionAssignments}
        />
      )
    }
  }
}

const mapStateToProps = state => {
  const denormalizedState = denormalize(state.api || {})

  const {
    "quest-progresses": questProgresses,
    "ngss-cccs": ngssCccs,
    "ngss-dcis": ngssDcis,
    "ngss-seps": ngssSeps,
    "work-product-summaries": workProductSummaries,
    "argument-builder-submissions": argumentBuilderSubmissions,
    "research-submissions": researchSubmissions,
    "flowchart-submissions": flowchartSubmissions,
    "heredity-probability-submissions": heredityProbabilitySubmissions,
    "picture-click-submissions": pictureClickSubmissions,
    "categorization-submissions": categorizationSubmissions,
    "frequency-chart-submissions": frequencyChartSubmissions,
    students,
    sections,
    courses,
    quests,
    modules,
    sequences
  } = denormalizedState

  const {
    committedCourseIds,
    committedSectionIds,
    committedStudentIds,
    committedUserUuids,
    committedStartingDate,
    committedEndingDate
  } = state.filter

  const {
    questId,
    selectedProgressOverviewShowAllContent,
    selectedProgressOverviewModuleIds,
    selectedProgressOverviewContentTypes,
    committedProgressOverviewShowAllContent,
    committedProgressOverviewModuleIds,
    committedProgressOverviewContentTypes,
    ngssDimensionsShowAllContent,
    ngssDimensionsContentType,
    performanceExpectationsShowAllContent,
    performanceExpectationsModuleIds,
    expeditionAssignments
  } = state.trackProgressRouting

  return {
    ngssCccs,
    ngssDcis,
    ngssSeps,
    questProgresses,
    sections,
    courses,
    students,
    quests,
    modules,
    sequences,
    questId,
    selectedProgressOverviewShowAllContent,
    selectedProgressOverviewModuleIds,
    selectedProgressOverviewContentTypes,
    committedProgressOverviewShowAllContent,
    committedProgressOverviewModuleIds,
    committedProgressOverviewContentTypes,
    committedCourseIds,
    committedSectionIds,
    committedStudentIds,
    committedUserUuids,
    committedStartingDate,
    committedEndingDate,
    ngssDimensionsShowAllContent,
    ngssDimensionsContentType,
    performanceExpectationsShowAllContent,
    performanceExpectationsModuleIds,
    workProductSummaries,
    argumentBuilderSubmissions,
    researchSubmissions,
    flowchartSubmissions,
    heredityProbabilitySubmissions,
    pictureClickSubmissions,
    categorizationSubmissions,
    frequencyChartSubmissions,
    readingCount: state.api.isReading,
    expeditionAssignments
  }
}

const { func, object } = PropTypes
TrackProgressRoutingContainer.propTypes = {
  dispatch: func.isRequired,
  match: object.isRequired
}

export { trackProgressPaths }
export default connect(mapStateToProps)(TrackProgressRoutingContainer)
